/**
 * What the exercise catalogue says about the movements a card draws: the muscle each trains, its
 * cover still and its type's label key. Read for the day form and for a change under review alike.
 *
 * It is the same 30-minute cache entry the week canvas already holds, so this costs no request. Its
 * failure is not the card's failure: a movement the catalogue does not carry simply draws without
 * a muscle line and a still, and a catalogue that has not arrived reads the same until it does.
 */

import { useQuery } from '@tanstack/react-query';

import {
  exerciseLibraryQueryOptions,
  heroStillUrl,
  toExerciseListItems,
  toMuscleAttribution,
  type ExerciseListItem,
} from '~/entities/exercise-library';
import type { MuscleAttributionMap } from '~/entities/plan-draft';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

/** The exercise-type label group, in the `web` namespace — see the note below. */
const TYPE_LABEL = 'web:planBuilder.day.exerciseType';

export interface ExerciseCatalogueFacts {
  readonly attribution: MuscleAttributionMap;
  /**
   * Exercise id → the still its card draws: the photograph when the catalogue has one, the demo
   * video's 3D poster frame when it does not, and `null` only when it names neither.
   */
  readonly thumbnails: ReadonlyMap<number, string | null>;
  /** Exercise id → its type's i18n key, or `null`. */
  readonly typeLabels: ReadonlyMap<number, string | null>;
}

/**
 * The type label group is the WEB namespace's, not the upstream `exerciseLibrary.type.*` one.
 *
 * Upstream carries five of the six — mobile's own union never had `Custom` — so reading it would
 * leave a trainer's own movement with a blank pill. Execution ADR §7 names mixing namespaces inside
 * one legend as the failure to avoid, so the whole group moves rather than one key being borrowed.
 */
function typeLabelKeys(items: readonly ExerciseListItem[]): ReadonlyMap<number, string | null> {
  return new Map(
    items.map((item) => [item.id, item.typeKey === null ? null : `${TYPE_LABEL}.${item.typeKey}`]),
  );
}

export function useExerciseCatalogue(): ExerciseCatalogueFacts {
  const contentLang = useContentLanguage();
  const library = useQuery(exerciseLibraryQueryOptions(contentLang));
  const items = toExerciseListItems(library.data);
  return {
    attribution: toMuscleAttribution(items),
    thumbnails: new Map(items.map((item) => [item.id, heroStillUrl(item)])),
    typeLabels: typeLabelKeys(items),
  };
}
